import { StateCreator } from 'zustand';
import { createAppStore } from '@marriott/mi-store-utils';
import { GetPropertyDetailsResponse } from '@marriott/mi-groups-graphql';

export type EventStatusFilters = {
  hasOldPlannerUpload: boolean;
  hasFreshPlannerUpload: boolean;
  hasAssociateUpload: boolean;
};

export type AssociateState = {
  loggedIn: boolean;
  marshaCode: string;
  uxlMigrationStatus: string;
  searchValue: string;
  filters: EventStatusFilters;
  currentPage: number;
  fetchEventList: boolean;
  propertyDetails: GetPropertyDetailsResponse;
  hasHydrated: boolean;
};

export type AssociateActions = {
  setLoggedIn: (loggedIn: boolean) => void;
  setMarshaCode: (marshaCode: string) => void;
  setUxlMigrationStatus: (UxlMigrationStatus: string) => void;
  setSearchValue: (searchValue: string) => void;
  setFilters: (filters: EventStatusFilters) => void;
  setCurrentPage: (currentPage: number) => void;
  resetState: () => void;
  setFetchEventList: (fetchEventList: boolean) => void;
  setPropertyDetails: (propertyDetails: GetPropertyDetailsResponse) => void;
  setHasHydrated: (hydrated: boolean) => void;
};

const initialState: AssociateState = {
  loggedIn: false,
  marshaCode: '',
  searchValue: '',
  uxlMigrationStatus: '',
  filters: {
    hasOldPlannerUpload: false,
    hasFreshPlannerUpload: true,
    hasAssociateUpload: false,
  },
  currentPage: 1,
  fetchEventList: false,
  propertyDetails: {
    id: '',
    basicInformation: {
      brand: {
        id: '',
        name: '',
      },
    },
    contactInformation: {
      address: {
        city: '',
        stateProvince: {
          code: '',
          description: '',
        },
        country: {
          code: '',
        },
      },
    },
  },
  hasHydrated: false,
};

export const associateStore: StateCreator<AssociateState & AssociateActions> = set => {
  return {
    ...initialState,

    setLoggedIn: (loggedIn: boolean) => {
      set(prevState => {
        return {
          ...prevState,
          loggedIn,
        };
      });
    },

    setMarshaCode: (marshaCode: string) => {
      set(prevState => {
        return {
          ...prevState,
          marshaCode,
        };
      });
    },

    setUxlMigrationStatus: (uxlMigrationStatus: string) => {
      set(prevState => {
        return {
          ...prevState,
          uxlMigrationStatus,
        };
      });
    },

    setSearchValue: (searchValue: string) => {
      set(prevState => {
        return {
          ...prevState,
          searchValue,
        };
      });
    },

    setFilters: (filters: EventStatusFilters) => {
      set(prevState => {
        return {
          ...prevState,
          filters,
        };
      });
    },

    setCurrentPage: (currentPage: number) => {
      set(prevState => {
        return {
          ...prevState,
          currentPage,
        };
      });
    },

    setFetchEventList: (fetchEventList: boolean) => {
      set(prevState => {
        return {
          ...prevState,
          fetchEventList,
        };
      });
    },

    setPropertyDetails: (propertyDetails: GetPropertyDetailsResponse) => {
      set(prevState => {
        return {
          ...prevState,
          propertyDetails,
        };
      });
    },

    resetState: () => {
      set(prevState => {
        return {
          ...prevState,
          currentPage: 1,
          marshaCode: '',
          uxlMigrationStatus: '',
          searchValue: '',
          loggedIn: false,
          fetchEventList: false,
        };
      });
    },

    setHasHydrated: (hydrated: boolean) => set(prevState => ({ ...prevState, hasHydrated: hydrated })),
  };
};

export const useAssociateStore = createAppStore(associateStore, {
  usePersistentStore: true,
  persistentStoreName: 'RoomingListAssociate',
  useLocalStorage: true,
  onRehydrateStorage: () => state => {
    state?.setHasHydrated(true);
  },
});
